import { createContext, useContext, useEffect, useMemo, useState, type ChangeEvent } from "react";
import axios from "axios";
import {
  Loader2,
  AlertTriangle,
  Clock,
  Users,
  FileText,
  Headphones,
  Calculator,
  ShieldCheck,
  Sparkles,
  Info,
  DollarSign,
  TrendingUp,
  Mic,
  Gauge,
  FileSignature,
  Video,
  ListChecks,
  LayoutTemplate,
  Settings2,
  Quote,
  Smartphone,
  Lock,
  Cloud,
  KeyRound,
  ScrollText,
  Layers,
  PenLine,
  Scale,
  CheckCircle2,
  MessageSquareReply,
} from "lucide-react";
import { useCountUp } from "./useCountUp";
import {
  MonthlySavedTrend,
  WorkflowComparison,
  OfficerLeaderboard,
  AudioBreakdown,
  ThroughputBars,
} from "./Charts";
import { CaseList } from "./CaseList";
import { CaseViewer } from "./CaseViewer";
import type { StudyBundle } from "./types";

// Bundle URL goes through our backend so the S3 bucket can stay private
// (block-public-access enabled, no public bucket policy). The backend reads
// the JSON with server credentials and streams it to the browser, logging
// every access for SOC 2 / CJIS compliance.
//
// REACT_APP_TRIAL_FINDINGS_URL takes priority for one-off overrides
// (e.g. testing against a specific staging bundle).
function bundleUrlFor(slug: string): string {
  const override = process.env.REACT_APP_TRIAL_FINDINGS_URL;
  if (override) return override;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend}/api/trial-findings/${slug}`;
}

// Fallback constants when the bundle JSON does not include a salaryBasis.
// These default to the figures published for Summit County / Akron OH so the
// older Summit bundle still renders correctly without the new fields.
const FALLBACK_PRIMARY_SALARY = 78761;
const FALLBACK_PRIMARY_LABEL = "Average officer salary";
const FALLBACK_PRIMARY_SOURCE = "Salary.com";

// Centralized period-aware label helper. "trial" reports keep the original
// pilot-evaluation framing; "annual" reports reframe as a retrospective
// department review. Returning a flat object instead of branching at each
// callsite keeps the section components readable.
type PeriodLabels = {
  isAnnual: boolean;
  diagnosticBanner: string;       // header eyebrow text
  headlineSuffix: string;          // h1 trailing clause
  proofTitle: string;              // §9 title
  proofTocLabel: string;           // TOC entry for §9
  periodNoun: string;              // "the trial" | "this year" / "the review period"
  periodNounCap: string;           // "Trial" | "Year"
  windowLabel: string;             // "Trial window" | "Review window"
};

function getPeriodLabels(bundle: StudyBundle): PeriodLabels {
  const isAnnual = bundle.report_type === "annual";
  return {
    isAnnual,
    diagnosticBanner: isAnnual
      ? "Annual department review · Police Narratives AI"
      : "Post-trial diagnostic · Police Narratives AI",
    headlineSuffix: isAnnual
      ? "report-writing time saving over the review period"
      : "report-writing time saving across the trial period",
    proofTitle: isAnnual ? "What this year proved" : "What the trial proved",
    proofTocLabel: isAnnual ? "What this year proved" : "What the trial proved",
    periodNoun: isAnnual ? "the review period" : "the trial",
    periodNounCap: isAnnual ? "Review" : "Trial",
    windowLabel: isAnnual ? "Review window" : "Trial window",
  };
}

// Section registry. `baseNum` is the canonical position used inside section
// components to look up their displayed number in the SectionMap. The exec
// summary intro doesn't get a number. Visibility predicates let a section
// be hidden entirely (TOC entry + section render + numbering all collapse
// together) — currently only Enhanced Writing is conditional, but adding
// more is a one-liner.
type SectionDef = {
  id: string;
  // Labels can depend on the bundle (e.g. "What the trial proved" vs
  // "What this year proved"). Plain strings are wrapped at lookup time.
  label: string | ((b: StudyBundle) => string);
  baseNum: number; // 0 = unnumbered (exec summary intro)
  visible: (b: StudyBundle) => boolean;
};

const SECTION_DEFS: SectionDef[] = [
  { id: "summary", label: "Executive summary", baseNum: 0, visible: () => true },
  { id: "platform", label: "Platform", baseNum: 0, visible: () => true },
  { id: "defensibility", label: "Defensibility", baseNum: 0, visible: () => true },
  { id: "sec-1", label: "Time saved", baseNum: 1, visible: () => true },
  { id: "sec-2", label: "How officers used it", baseNum: 2, visible: () => true },
  { id: "sec-3", label: "Reports kept in the platform", baseNum: 3, visible: () => true },
  {
    id: "sec-4",
    label: "Enhanced writing",
    baseNum: 4,
    visible: (b) => b.enhancedWriting.cases > 0,
  },
  {
    // Suggestions feature adoption section — inserted between Enhanced
    // Writing (§4) and Top officers (§5). Uses baseNum 11 to avoid
    // renumbering existing sections; the buildSectionMap ordinal handles
    // display numbering off of position in this array.
    id: "sec-suggestions",
    label: "Suggestions feature usage",
    baseNum: 11,
    visible: (b) =>
      (b.suggestionsUsage?.implemented_cases ?? 0) > 0 ||
      (b.suggestionsUsage?.address_cases ?? 0) > 0,
  },
  { id: "sec-5", label: "Top officers", baseNum: 5, visible: () => true },
  {
    // End-of-trial officer survey — between Top officers and the Case
    // index. baseNum 12 keeps existing sections' numbers stable; display
    // numbering comes from position in this array.
    id: "sec-survey",
    label: "What officers said",
    baseNum: 12,
    visible: (b) => (b.survey?.responses ?? 0) > 0,
  },
  { id: "sec-6", label: "Case index", baseNum: 6, visible: () => true },
  { id: "sec-7", label: "Methodology", baseNum: 7, visible: () => true },
  {
    id: "sec-8",
    label: "Cost projection",
    baseNum: 8,
    // Hidden on annual department reviews — chiefs reading the annual
    // report shouldn't see a public sales-style cost projection. Trial
    // reports keep it as the pitch for expanding to department scale.
    visible: (b) => b.report_type !== "annual",
  },
  {
    id: "sec-9",
    label: (b) => getPeriodLabels(b).proofTocLabel,
    baseNum: 9,
    visible: () => true,
  },
];

// baseNum → display number (1-indexed, gaps closed by visibility filter).
type SectionMap = Record<number, number>;
const SectionNumContext = createContext<SectionMap>({});
const useSectionNum = (baseNum: number): number =>
  useContext(SectionNumContext)[baseNum] ?? baseNum;

function buildSectionMap(bundle: StudyBundle): SectionMap {
  let display = 0;
  const map: SectionMap = {};
  for (const def of SECTION_DEFS) {
    if (def.baseNum === 0) continue;
    if (!def.visible(bundle)) continue;
    display++;
    map[def.baseNum] = display;
  }
  return map;
}

// 2,080 hours = 40 hr × 52 wk — standard US labor-rate denominator.
const HOURS_PER_WORK_YEAR = 2080;
// Default headcount for the cost projection — tuned per the original Summit
// trial; the user can edit it live in the page's Cost Projection section.
const DEFAULT_PROJECTION_OFFICERS = 70;
// FLSA standard: police overtime is paid at 1.5× regular hourly rate.
// Post-shift report writing is FLSA-compensable, so report time that pushes
// an officer past their work-period threshold legitimately becomes overtime.
const OT_MULTIPLIER = 1.5;
// Default share of recovered hours that would have been worked at OT rates.
// "A lot of it will be overtime, but not all" — 50% is a defensible midpoint.
const DEFAULT_OT_MIX_PCT = 50;

export default function TrialFindingsReport({ slug }: { slug: string }) {
  const [bundle, setBundle] = useState<StudyBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState<
    { kind: "not_found" } | { kind: "other"; message: string } | null
  >(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  const dataUrl = useMemo(() => bundleUrlFor(slug), [slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorState(null);
    setBundle(null);
    axios
      .get<StudyBundle>(dataUrl, { withCredentials: false })
      .then((r) => {
        if (!cancelled) setBundle(r.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setErrorState({ kind: "not_found" });
        } else {
          setErrorState({
            kind: "other",
            message: err?.message || `Failed to load study data from ${dataUrl}.`,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataUrl]);

  const selectedCase = useMemo(
    () => bundle?.cases.find((c) => c.id === selectedCaseId) ?? null,
    [bundle, selectedCaseId]
  );

  // Compute section numbering before early returns so React hooks fire in
  // a consistent order on every render.
  const sectionMap = useMemo(
    () => (bundle ? buildSectionMap(bundle) : {}),
    [bundle]
  );

  if (loading) return <LoadingState />;
  if (errorState?.kind === "not_found") return <NotFoundState slug={slug} />;
  if (errorState) return <ErrorState message={errorState.message} />;
  if (!bundle) return null;

  const enhancedWritingVisible = bundle.enhancedWriting.cases > 0;
  const surveyVisible = (bundle.survey?.responses ?? 0) > 0;
  const suggestionsUsageVisible =
    (bundle.suggestionsUsage?.implemented_cases ?? 0) > 0 ||
    (bundle.suggestionsUsage?.address_cases ?? 0) > 0;

  return (
    <SectionNumContext.Provider value={sectionMap}>
    <div className="min-h-screen bg-white text-slate-900">
      <StudyHeader bundle={bundle} />
      <div className="max-w-5xl mx-auto px-6 pb-24">
        <TableOfContents bundle={bundle} />
        <ExecutiveSummary bundle={bundle} />
        <PlatformFeaturesSection bundle={bundle} />
        <DefensibilitySection />
        <TimeSavedSection bundle={bundle} />
        <UsageSection bundle={bundle} />
        <PlatformConsolidationSection bundle={bundle} />
        {/* Hide §4 entirely when no Enhanced Writing was used during the
            trial — empty stats here read as "nobody used this feature"
            which undercuts the report. The SectionMap closes the numbering
            gap automatically. */}
        {enhancedWritingVisible && <EnhancedWritingSection bundle={bundle} />}
        {suggestionsUsageVisible && <SuggestionsUsageSection bundle={bundle} />}
        <OfficersSection bundle={bundle} />
        {surveyVisible && <SurveySection bundle={bundle} />}
        <CaseIndexSection bundle={bundle} onSelect={setSelectedCaseId} />
        <MethodologySection bundle={bundle} />
        {/* §8 Cost projection is a sales-style projection — hidden on
            annual reviews to avoid surfacing a public-facing scaling
            pitch to active departments. TOC numbering closes the gap. */}
        {bundle.report_type !== "annual" && (
          <CostProjectionSection bundle={bundle} />
        )}
        <ProofSection bundle={bundle} />
      </div>
      {selectedCase && (
        <CaseViewer
          detail={selectedCase}
          setupMin={bundle.constants.setup_min_per_report ?? 0}
          typingWpm={bundle.constants.typing_wpm}
          reviewMin={bundle.constants.review_min_per_case}
          relistenSpeed={bundle.constants.relisten_speed}
          onClose={() => setSelectedCaseId(null)}
        />
      )}
    </div>
    </SectionNumContext.Provider>
  );
}

// ================== Header ====================

function StudyHeader({ bundle }: { bundle: StudyBundle }) {
  const labels = getPeriodLabels(bundle);
  // Annual reports use the user-picked window for the header range so the
  // report covers exactly what the operator selected (not just the
  // first/last case timestamp in that window).
  const start = bundle.window?.start
    ? new Date(bundle.window.start)
    : new Date(bundle.summary.first_case);
  const end = bundle.window?.end
    ? new Date(bundle.window.end)
    : new Date(bundle.summary.last_case);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  const generatedDate = new Date(bundle.generated_at);

  return (
    <header className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-slate-900 to-blue-900 text-white border-b border-slate-900">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <BackgroundGrid />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 py-14 md:py-20">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-emerald-300/90 font-medium mb-4">
          <span className="inline-block w-6 h-px bg-emerald-400/60" />
          {labels.diagnosticBanner}
        </div>
        <h1 className="text-3xl md:text-[40px] font-semibold tracking-tight text-white leading-snug max-w-3xl">
          {bundle.agency.name}: a {(bundle.summary.saved_min_total / 60).toFixed(0)}-hour{" "}
          {labels.headlineSuffix}
        </h1>

        <dl className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 text-sm border-t border-white/10 pt-8">
          <MetaField label="Prepared for" value={bundle.agency.name} />
          <MetaField label="Agency ID" value={bundle.agency.id} mono />
          <MetaField label={labels.windowLabel} value={`${fmt(start)} → ${fmt(end)}`} />
          <MetaField label="Report date" value={fmt(generatedDate)} />
          <MetaField
            label="Reports written"
            value={bundle.summary.total_cases_all.toLocaleString()}
          />
          {/* Officer headcount intentionally hidden for annual reviews:
              not every sworn officer uses the platform during the
              window, and surfacing that ratio invites seat-reduction
              conversations. Kept on trial reports where partial
              adoption is expected and the count is informative. */}
          {!labels.isAnnual && (
            <MetaField
              label="Officers using"
              value={bundle.summary.total_officers_all.toString()}
            />
          )}
          <MetaField
            label="Total words documented"
            value={bundle.summary.total_words_all.toLocaleString()}
          />
          <MetaField
            label="Audio captured"
            value={`${(
              (bundle.summary.narrative_audio_sec + bundle.summary.interaction_audio_sec) /
              3600
            ).toFixed(1)} hr`}
          />
          {/* Header meta is the same in both modes — total audio is already
              an aggregate, no narrative/interaction split shown here. */}
        </dl>
      </div>
    </header>
  );
}

function MetaField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.14em] text-emerald-300/80 font-medium">
        {label}
      </dt>
      <dd
        className={`mt-1 text-slate-100 ${
          mono ? "font-mono text-[12.5px] break-all" : "text-[13.5px]"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function BackgroundGrid() {
  return (
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <pattern id="scso-grid" width="48" height="48" patternUnits="userSpaceOnUse">
          <path
            d="M 48 0 L 0 0 0 48"
            fill="none"
            stroke="rgba(110, 231, 183, 0.30)"
            strokeWidth="0.5"
          />
        </pattern>
        <radialGradient id="scso-fade" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="white" stopOpacity="0.55" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#scso-grid)" />
      <rect width="100%" height="100%" fill="url(#scso-fade)" opacity="0.4" />
    </svg>
  );
}

// ================== Table of Contents ====================

function TableOfContents({ bundle }: { bundle: StudyBundle }) {
  // Skip TOC entries for sections that won't render (so the link doesn't
  // dead-end), and prefix each visible section's label with its dynamic
  // display number from the SectionMap (so numbering stays sequential).
  const sectionMap = useContext(SectionNumContext);
  const visible = SECTION_DEFS.filter((d) => d.visible(bundle));
  return (
    <nav className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-white/85 backdrop-blur border-b border-slate-200">
      <ol className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
        {visible.map((d) => {
          const label = typeof d.label === "function" ? d.label(bundle) : d.label;
          return (
            <li key={d.id}>
              <a href={`#${d.id}`} className="hover:text-blue-700 transition-colors">
                {d.baseNum > 0 ? `${sectionMap[d.baseNum]}. ${label}` : label}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ================== Section shell ====================

function Section({
  id,
  number,
  title,
  subtitle,
  children,
}: {
  id?: string;
  number?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="pt-14 md:pt-20 scroll-mt-16">
      <div className="flex items-baseline gap-3 mb-2">
        {number && (
          <span className="text-sm font-mono text-slate-400 tabular-nums">{number}</span>
        )}
        <h2 className="text-2xl md:text-[28px] font-semibold tracking-tight text-slate-900 leading-tight">
          {title}
        </h2>
      </div>
      {subtitle && (
        <p className="text-slate-600 leading-relaxed max-w-3xl mb-6">{subtitle}</p>
      )}
      {children}
    </section>
  );
}

function SubSection({
  number,
  title,
  subtitle,
  children,
}: {
  number: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-10">
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-xs font-mono text-slate-400 tabular-nums">{number}</span>
        <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      </div>
      {subtitle && (
        <p className="text-slate-600 text-sm leading-relaxed max-w-3xl mb-4">{subtitle}</p>
      )}
      {children}
    </div>
  );
}

// ================== Platform Features ====================

function PlatformFeaturesSection({ bundle }: { bundle: StudyBundle }) {
  const labels = getPeriodLabels(bundle);
  return (
    <Section
      id="platform"
      title="What officers had access to"
      subtitle={`Every feature listed below was live and available to the agency throughout ${labels.periodNoun}. Officers could use as much or as little as fit their workflow.`}
    >
      {/* Featured row — AI Narrative is the headline capability */}
      <div className="rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-6 mb-3">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
              <Sparkles size={14} />
              Primary workflow
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2 tracking-tight">
              AI Narrative Generation
            </h3>
            <p className="text-sm text-slate-700 leading-relaxed max-w-2xl">
              Audio captured by the officer — either a dictated narrative or a recorded
              interaction — is transcribed and passed to a large language model that
              produces a structured first-draft report. Output reflects each officer's
              configured preferences and any selected agency template.
            </p>
          </div>
          <div className="flex items-end gap-1 h-20 shrink-0">
            {[40, 65, 90, 50, 75, 95, 55, 80, 35, 70, 50, 85].map((h, i) => (
              <span
                key={i}
                className="w-1.5 rounded-full bg-emerald-500/70"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Tier 2 — primary AI features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <FeatureCard
          icon={<Gauge size={16} />}
          title="Report Scoring"
          body="Each report is evaluated against a fixed rubric of report-writing fundamentals, with the resulting score and inline suggestions surfaced in the editor for the officer to review."
        />
        <FeatureCard
          icon={<Users size={16} />}
          title="Speaker Diarization"
          body="Interaction audio (interviews, body cam, witness statements) is transcribed with speaker labels, distinguishing officer, subject, and other speakers in the resulting transcript."
        />
        <FeatureCard
          icon={<FileSignature size={16} />}
          title="PDF Form Designer & AI Fill"
          body="Agency PDFs can be uploaded once and configured with field positions; subsequent submissions are populated from a case's data without manual re-entry."
        />
      </div>

      {/* Tier 3 — workflow features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <FeatureCard
          icon={<Video size={16} />}
          title="Body Cam Integration"
          body="Footage is transferred from Axon Evidence directly into the platform without re-upload. Audio is extracted server-side and routed to transcription."
        />
        <FeatureCard
          icon={<ListChecks size={16} />}
          title="Checklists"
          body="Per-incident-type checklists attach to a case to track required documentation steps. Templates can be configured at the agency level."
        />
        <FeatureCard
          icon={<Quote size={16} />}
          title="Snippets"
          body="Frequently reused passages — Miranda warnings, standard descriptions, agency-specific boilerplate — are stored as snippets and inserted from the editor."
        />
      </div>

      {/* Tier 4 — personalization & sync */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <FeatureCard
          icon={<Settings2 size={16} />}
          title="Report Preferences"
          body="Per-officer settings — perspective, time and date formats, name capitalization, statute state, and others — are sent to the model on every generation so output matches the officer's style."
        />
        <FeatureCard
          icon={<LayoutTemplate size={16} />}
          title="Report Templates"
          body="Reusable header, footer, and body-structure templates. Officers can author personal templates or use agency-wide templates configured by an admin."
        />
        <FeatureCard
          icon={<Smartphone size={16} />}
          title="Mobile + Desktop"
          body="Cases recorded on the mobile app appear on the desktop client without manual transfer, supporting capture in the field and review at the station."
        />
      </div>

      {/* Security & compliance strip */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
          <ShieldCheck size={14} />
          Security &amp; compliance
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <ComplianceBadge icon={<ShieldCheck size={14} />} label="CJIS Compliance" />
          <ComplianceBadge icon={<Cloud size={14} />} label="AWS GovCloud" />
          <ComplianceBadge icon={<KeyRound size={14} />} label="MFA + SSO (SAML/OIDC)" />
          <ComplianceBadge icon={<Lock size={14} />} label="256-bit Encryption" />
          <ComplianceBadge icon={<ScrollText size={14} />} label="Full Audit Trails" />
        </div>
      </div>
    </Section>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-blue-50 text-blue-700">
          {icon}
        </span>
        <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">
          {title}
        </h3>
      </div>
      <p className="text-[13px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function ComplianceBadge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md bg-white border border-slate-200 px-3 py-2">
      <span className="text-emerald-700 shrink-0">{icon}</span>
      <span className="text-[12px] font-medium text-slate-700 leading-tight">{label}</span>
    </div>
  );
}

// ================== Defensibility ====================

function DefensibilitySection() {
  return (
    <Section
      id="defensibility"
      title="Defensibility in court"
      subtitle="A note on workflow design. The platform is built around the officer's voice, not around AI summarization of body cam footage, and that distinction is what keeps the resulting reports defensible on the stand."
    >
      <div className="prose prose-slate max-w-none prose-p:leading-relaxed prose-p:text-slate-700 mb-6">
        <p>
          Reports written in Police Narratives AI are <b>authored by the officer through their
          voice</b>. The model's role is to transcribe, structure, and apply formatting
          conventions; it is not positioned to substitute for the officer's recall, judgment, or
          articulation. The cognitive work that report writing has always required of an officer
          stays with the officer, and so does the ability to testify to what was written.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <DefensibilityPillar
          icon={<Mic size={16} />}
          title="Officer-dictated narrative"
          body="The primary input to the platform is the officer's voice describing what they observed. The substantive content of every report originates with the officer; the AI transcribes that audio and applies the agency's formatting standards on top of it."
        />
        <DefensibilityPillar
          icon={<Layers size={16} />}
          title="Body cam paired with narrative audio"
          body="On cases where body cam audio is the input, the officer can pair it with a separately recorded narrative audio. The report is generated from both, combining the raw recording with the officer's first-person account, rather than relying on a model's summary of footage alone."
        />
        <DefensibilityPillar
          icon={<PenLine size={16} />}
          title="Officer review and sign-off"
          body="Every AI-generated draft is opened in the editor for the officer to review, edit, and approve before the report is filed. The officer is the final author of the document of record, not the model."
        />
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-emerald-50 text-emerald-700 shrink-0 mt-0.5">
            <Scale size={16} />
          </span>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              On the stand
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Because the officer dictates, reviews, and signs off on every report, they remain
              in a position to testify that the document reflects their own observations and
              recollection. The original audio file and the corresponding transcript are both
              retained alongside the case, providing a verifiable chain from observation to
              filed report, useful for both internal review and for any subsequent court
              inquiry.
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
}

function DefensibilityPillar({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-50 text-emerald-700">
          {icon}
        </span>
        <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">
          {title}
        </h3>
      </div>
      <p className="text-[13px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

// ================== Executive Summary ====================

function ExecutiveSummary({ bundle }: { bundle: StudyBundle }) {
  const { summary } = bundle;
  const labels = getPeriodLabels(bundle);
  const timeSavedNum = useSectionNum(1);
  const consolidationNum = useSectionNum(3);
  const surveyNum = useSectionNum(12);
  const savedHr = useCountUp(summary.saved_min_total / 60, 1500, 1);
  const cases = useCountUp(summary.cases, 1200);
  const audioHr = useCountUp(
    (summary.narrative_audio_sec + summary.interaction_audio_sec) / 3600,
    1500,
    1
  );

  const avgSavedPerCase = summary.cases > 0 ? summary.saved_min_total / summary.cases : 0;
  const avgPerOfficer =
    summary.active_officers > 0 ? summary.saved_min_total / 60 / summary.active_officers : 0;
  // Rounded duration of the reporting window in whole months, used for the
  // singular/plural agreement in the intro paragraph.
  const summaryMonths = Math.round(
    (new Date(summary.last_case_all).getTime() -
      new Date(summary.first_case_all).getTime()) /
      (1000 * 60 * 60 * 24 * 30)
  );

  return (
    <Section id="summary" title="Executive summary">
      {/* Annual reports drop the "Officers using" stat — partial adoption
          is normal for active departments and surfacing the headcount
          ratio invites seat-reduction asks. Collapse the grid to 3 cols
          to avoid an empty slot. */}
      <div
        className={`grid grid-cols-2 ${
          labels.isAnnual ? "md:grid-cols-3" : "md:grid-cols-4"
        } gap-3 mb-6`}
      >
        <HeroStat
          icon={<Clock size={18} />}
          label="Hours saved"
          value={savedHr.toFixed(1)}
          unit="hr"
          sub={`≈ ${avgSavedPerCase.toFixed(1)} min per AI-assisted case`}
          accent
        />
        <HeroStat
          icon={<FileText size={18} />}
          label="Reports written"
          value={summary.total_cases_all.toLocaleString()}
          sub={`${cases.toLocaleString()} AI-assisted · ${bundle.excluded.cases.toLocaleString()} typed in platform`}
        />
        {!labels.isAnnual && (
          <HeroStat
            icon={<Users size={18} />}
            label="Officers using"
            value={summary.total_officers_all.toLocaleString()}
            sub={`avg ${avgPerOfficer.toFixed(1)} hr saved per officer`}
          />
        )}
        <HeroStat
          icon={<Headphones size={18} />}
          label="Audio captured"
          value={audioHr.toFixed(1)}
          unit="hr"
          sub={
            bundle.obscure_audio_split
              ? `${(summary.narrative_files + summary.interaction_files).toLocaleString()} file${
                  summary.narrative_files + summary.interaction_files === 1 ? "" : "s"
                }`
              : `${summary.narrative_files} narrative · ${summary.interaction_files} interaction`
          }
        />
      </div>

      <div className="prose prose-slate max-w-none prose-p:leading-relaxed prose-p:text-slate-700">
        <p>
          Across the {summaryMonths} {summaryMonths === 1 ? "month" : "months"} of{" "}
          {labels.periodNoun}, {bundle.agency.name} officers wrote{" "}
          <b>{summary.total_cases_all.toLocaleString()}</b> reports inside Police Narratives AI
          —{" "}<b>{summary.total_words_all.toLocaleString()} words</b> of incident documentation
          captured in a single, indexed system instead of scattered Word documents. Of those,{" "}
          <b>{summary.cases.toLocaleString()}</b> cases used the platform's AI features
          (dictation, body cam transcription, suggestion review, AI scoring), recovering an
          estimated <b>{(summary.saved_min_total / 60).toFixed(0)} hours</b> of officer
          report-writing time — about <b>{avgSavedPerCase.toFixed(0)} minutes per AI-assisted
          case</b>.
        </p>
        {bundle.executive_summary ? (
          // LLM-generated, agency-specific second paragraph. Bundle ships it
          // as an HTML string (with <b>/<i> for emphasis); content is
          // generated server-side from a constrained Claude prompt.
          <p dangerouslySetInnerHTML={{ __html: bundle.executive_summary }} />
        ) : (
          <p>
            Time savings scaled with case volume — as more reports went through the platform,
            monthly hours-saved climbed accordingly (see §{timeSavedNum}.3).
            {bundle.excluded.cases > 0 && (
              <>
                {" "}
                The{" "}
                {bundle.excluded.cases.toLocaleString()} reports written without AI assistance
                still benefit the agency by keeping all incident documentation in one
                searchable system — see §{consolidationNum} for that breakdown.
              </>
            )}
          </p>
        )}
        {bundle.survey && bundle.survey.responses > 0 && (
          <p>
            Officers were also asked directly.{" "}
            {bundle.survey.keep.pct_yes_or_probably !== null && (
              <>
                <b>{bundle.survey.keep.pct_yes_or_probably}%</b> of the{" "}
                {bundle.survey.responses} who responded want the department to keep the
                platform
              </>
            )}
            {bundle.survey.keep.pct_yes_or_probably !== null &&
              bundle.survey.nps.score !== null &&
              ", "}
            {bundle.survey.nps.score !== null && (
              <>
                {bundle.survey.keep.pct_yes_or_probably === null && "they "}
                {bundle.survey.keep.pct_yes_or_probably !== null && "and they "}
                gave it a Net Promoter Score of <b>{bundle.survey.nps.score}</b>
              </>
            )}
            {" "}— see §{surveyNum}.
          </p>
        )}
      </div>
    </Section>
  );
}

function HeroStat({
  icon,
  label,
  value,
  unit,
  sub,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-md p-4 border ${
        accent
          ? "bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200"
          : "bg-slate-50 border-slate-200"
      }`}
    >
      <div
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold ${
          accent ? "text-emerald-700" : "text-slate-500"
        }`}
      >
        {icon}
        {label}
      </div>
      <div className={`mt-2 text-2xl md:text-[28px] font-bold tabular-nums leading-tight ${accent ? "text-emerald-700" : "text-slate-900"}`}>
        {value}
        {unit && <span className="text-base font-medium ml-1 opacity-70">{unit}</span>}
      </div>
      {sub && (
        <div className={`text-[11px] mt-1 ${accent ? "text-emerald-700/70" : "text-slate-500"}`}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ================== 1. Time saved ====================

function TimeSavedSection({ bundle }: { bundle: StudyBundle }) {
  const { summary, byClass, byMonth } = bundle;
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(1);
  const methodologyNum = useSectionNum(7);

  return (
    <Section
      id="sec-1"
      number={String(num)}
      title="Time saved"
      subtitle={
        labels.isAnnual
          ? `The headline finding of the review, modeled from real reports written and real audio captured over the review period. See §${methodologyNum} for the calculation method.`
          : `The headline finding of the trial, modeled from real reports written and real audio captured during the trial period. See §${methodologyNum} for the calculation method.`
      }
    >
      <SubSection
        number={`${num}.1`}
        title="Manual workflow vs AI-assisted workflow"
        subtitle="What officers would have spent typing reports and re-listening to audio, vs. what they actually spent reviewing AI-generated drafts."
      >
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <BigCompare
            label="Without the platform"
            value={`${(summary.manual_min_total / 60).toFixed(0)} hr`}
            sub="typing + re-listening"
            color="slate"
          />
          <BigCompare
            label="With the platform"
            value={`${(summary.ai_min_total / 60).toFixed(0)} hr`}
            sub="reviewing AI drafts"
            color="blue"
          />
          <BigCompare
            label="Time recovered"
            value={`${(summary.saved_min_total / 60).toFixed(0)} hr`}
            sub={`${(summary.saved_min_total / summary.cases).toFixed(0)} min per case`}
            color="emerald"
          />
        </div>
      </SubSection>

      <SubSection
        number={`${num}.2`}
        title="By workflow"
        subtitle={
          bundle.obscure_audio_split
            ? "Time savings broken down by report-writing workflow. AI-assisted reports drive most of the recovery, with additional savings from Enhanced Writing on typed drafts."
            : "Time savings broken down by how each report was written. Interaction-audio cases produce the largest per-case recovery because officers no longer have to re-listen to the original incident audio."
        }
      >
        <div className="border border-slate-200 rounded-md p-5 bg-white">
          <WorkflowComparison
            rows={byClass}
            obscureAudioSplit={bundle.obscure_audio_split}
          />
        </div>
      </SubSection>

      <SubSection
        number={`${num}.3`}
        title="Monthly trend"
        subtitle="Hours saved by month. Monthly recovery scaled alongside steady adoption of the platform."
      >
        <div className="border border-slate-200 rounded-md p-5 bg-white">
          <MonthlySavedTrend data={byMonth} snapshotDate={bundle.generated_at} />
          <div className="text-xs text-slate-500 mt-3 leading-relaxed">
            The final data point reflects only the cases written before the snapshot date used
            to build this report — the dashed segment indicates a partial month, not a drop-off
            in usage.{labels.isAnnual ? "" : " The trial may still be ongoing."}
          </div>
        </div>
      </SubSection>
    </Section>
  );
}

function BigCompare({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: "slate" | "blue" | "emerald";
}) {
  const map = {
    slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", label: "text-slate-500" },
    blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", label: "text-blue-600" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "text-emerald-600" },
  }[color];
  return (
    <div className={`rounded-md p-5 border ${map.bg} ${map.border}`}>
      <div className={`text-[11px] uppercase tracking-wider font-semibold ${map.label}`}>{label}</div>
      <div className={`mt-2 text-3xl font-bold tabular-nums ${map.text}`}>{value}</div>
      <div className={`text-xs mt-1 ${map.label}`}>{sub}</div>
    </div>
  );
}

// ================== 2. Usage ====================

function UsageSection({ bundle }: { bundle: StudyBundle }) {
  const { summary, byMonth } = bundle;
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(2);

  return (
    <Section
      id="sec-2"
      number={String(num)}
      title="How officers used the platform"
      subtitle={`Volume of cases written and audio captured over ${labels.periodNoun}.`}
    >
      <SubSection
        number={`${num}.1`}
        title="Audio captured"
        subtitle={
          bundle.obscure_audio_split
            ? `Total audio that officers captured during ${labels.periodNoun} — transcribed by the platform and used to draft reports.`
            : "Two distinct workflows the platform handles: officers dictating their own narrative, and officers uploading the actual interaction (interview / body cam) for the AI to summarize."
        }
      >
        <AudioBreakdown
          narrativeSec={summary.narrative_audio_sec}
          interactionSec={summary.interaction_audio_sec}
          obscureAudioSplit={bundle.obscure_audio_split}
        />
      </SubSection>

      <SubSection
        number={`${num}.2`}
        title="Cases per month"
        subtitle={
          labels.isAnnual
            ? "Monthly throughput across the review period — usage held steady month over month, evidence that the workflow remained part of officers' routine rather than fading off."
            : "Trial throughput grew steadily — a signal that the workflow stuck, rather than a brief curiosity period followed by drop-off."
        }
      >
        <div className="border border-slate-200 rounded-md p-5 bg-white">
          <ThroughputBars data={byMonth} />
        </div>
      </SubSection>
    </Section>
  );
}

// ================== 3. Reports kept in the platform ====================

function PlatformConsolidationSection({ bundle }: { bundle: StudyBundle }) {
  const { summary, excluded } = bundle;
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(3);
  const totalCases = summary.total_cases_all;
  const aiCases = summary.cases;
  const typedCases = excluded.cases;
  const aiPct = totalCases > 0 ? (aiCases / totalCases) * 100 : 0;
  const typedPct = totalCases > 0 ? (typedCases / totalCases) * 100 : 0;
  const totalWords = summary.total_words_all;
  const typedWords = excluded.words;
  const typedOnlyOfficers = excluded.officers;

  return (
    <Section
      id="sec-3"
      number={String(num)}
      title="Reports kept in the platform"
      subtitle={
        labels.isAnnual
          ? "The hours-saved figure above only counts AI-assisted reports — but every report written with Police Narratives AI, including those typed manually, contributes to a single, indexed record of agency documentation. This section accounts for that broader value."
          : "The trial generated time savings on AI-assisted reports — but every report written with Police Narratives AI, even those typed manually, contributes to a single, indexed record of agency documentation. This section accounts for that broader value."
      }
    >
      {/* AI vs typed split bar */}
      <div className="rounded-md border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
          All {totalCases.toLocaleString()} reports written in PNAI
        </div>
        <div className="h-9 rounded-md overflow-hidden flex shadow-sm">
          <div
            className="flex items-center justify-center text-xs font-semibold text-white transition-all duration-1000"
            style={{ width: `${aiPct}%`, backgroundColor: "#1a78c2" }}
          >
            {aiPct >= 12 && `${aiCases.toLocaleString()} AI-assisted (${aiPct.toFixed(0)}%)`}
          </div>
          <div
            className="flex items-center justify-center text-xs font-semibold text-white transition-all duration-1000"
            style={{ width: `${typedPct}%`, backgroundColor: "#64748b" }}
          >
            {typedPct >= 12 && `${typedCases.toLocaleString()} typed (${typedPct.toFixed(0)}%)`}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#1a78c2" }} />
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                AI-assisted reports
              </span>
            </div>
            <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1">
              {aiCases.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {summary.total_words.toLocaleString()} words ·{" "}
              {(summary.saved_min_total / 60).toFixed(0)} hr saved
            </div>
          </div>
          <div className="border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#64748b" }} />
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Typed in platform
              </span>
            </div>
            <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1">
              {typedCases.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {typedWords.toLocaleString()} words · written by{" "}
              {typedOnlyOfficers} officer{typedOnlyOfficers === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </div>

      {/* Why typed cases still matter */}
      <SubSection
        number={`${num}.1`}
        title="Why typed reports still count"
        subtitle="Time savings only accrue on AI-assisted reports — but consolidating all incident documentation into a single platform delivers operational value the typing-vs-Word comparison usually misses."
      >
        <div className="grid md:grid-cols-2 gap-3">
          <ConsolidationCard
            title="One searchable record"
            body={`All ${totalCases.toLocaleString()} reports — AI-assisted and typed alike — sit in one indexed system. Records and admin staff find any case in seconds, instead of hunting through email attachments and shared drives for the right Word document.`}
          />
          <ConsolidationCard
            title="Audit trail by default"
            body="Every edit, signature, export, and access event is logged automatically. With Word documents, an officer's report exists in whatever state was last saved — no history, no provenance, no defensible chain of revisions."
          />
          <ConsolidationCard
            title="Standard structure"
            body={`Even typed-from-scratch reports use the agency's templates and formatting. ${totalWords.toLocaleString()} words of incident documentation now share the same structure, headings, and field layout — easier to review, easier to redact, easier to hand to prosecutors.`}
          />
          <ConsolidationCard
            title="A veteran supervisor in your corner"
            body="Even on reports an officer types themselves, the platform's AI tools are one click away — Suggestions and Address Suggestions catch missing details and inconsistencies, Enhanced Writing and Enhanced Dictation tighten structure and tone, and the AI Quality Score grades each report against best practices. It's like having a veteran supervisor reading every report before it goes out."
          />
        </div>
      </SubSection>

      {typedCases > 0 && (
        <div className="mt-8 rounded-lg border border-blue-200 bg-blue-50 p-5 flex items-start gap-3">
          <Info className="text-blue-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-900 leading-relaxed">
            <div className="font-semibold mb-1">Bottom line</div>
            {labels.isAnnual ? "This year, the AI features delivered on " : "The trial proved the AI features work on "}
            <b>{aiCases.toLocaleString()} reports</b> — but the agency now has{" "}
            <b>{totalCases.toLocaleString()} total reports</b> living in one searchable, auditable
            system, including the {typedCases.toLocaleString()} that were typed without AI. That
            consolidation is a separate win from time savings, and it scales with every officer
            who keeps writing reports here instead of in Word.
          </div>
        </div>
      )}
    </Section>
  );
}

function ConsolidationCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-900 mb-1">{title}</div>
      <div className="text-xs text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

// ================== 4. Enhanced writing ====================

function EnhancedWritingSection({ bundle }: { bundle: StudyBundle }) {
  const { enhancedWriting, summary } = bundle;
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(4);
  // SQL AVG returns NULL when no rows match; defensively coalesce here so
  // older bundles with the un-coalesced avg field don't crash the page.
  const avgActionsPerCase = enhancedWriting.avg_actions_per_case ?? 0;
  const pctOfCases = summary.cases > 0 ? (enhancedWriting.cases / summary.cases) * 100 : 0;
  const pctOfOfficers =
    summary.active_officers > 0 ? (enhancedWriting.officers / summary.active_officers) * 100 : 0;

  return (
    <Section
      id="sec-4"
      number={String(num)}
      title="Enhanced writing usage"
      subtitle={`Beyond audio transcription, the platform offers an Enhanced Writing feature that improves text the officer has typed — fixing structure, grammar, and tone with one tap. Adoption of this specific feature during ${labels.periodNoun} was modest, suggesting room for training-driven growth.`}
    >
      {/* Annual reports drop the "Officers using it" card so partial
          adoption isn't surfaced as a seat-reduction signal. Grid
          collapses to 3 cols accordingly. */}
      <div
        className={`grid grid-cols-2 ${
          labels.isAnnual ? "md:grid-cols-3" : "md:grid-cols-4"
        } gap-3 mb-6`}
      >
        <HeroStat
          icon={<Sparkles size={18} />}
          label="Cases enhanced"
          value={enhancedWriting.cases.toString()}
          sub={`${pctOfCases.toFixed(1)}% of AI-assisted cases`}
        />
        {!labels.isAnnual && (
          <HeroStat
            icon={<Users size={18} />}
            label="Officers using it"
            value={enhancedWriting.officers.toString()}
            sub={`${pctOfOfficers.toFixed(0)}% of active officers`}
          />
        )}
        <HeroStat
          icon={<Sparkles size={18} />}
          label="Total enhancements"
          value={enhancedWriting.total_actions.toString()}
          sub={`avg ${avgActionsPerCase.toFixed(1)} per case`}
        />
        <HeroStat
          icon={<FileText size={18} />}
          label="Words touched"
          value={enhancedWriting.words.toLocaleString()}
          sub="across enhanced cases"
        />
      </div>

      <div className="rounded-md border border-cyan-200 bg-cyan-50 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="text-cyan-700 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-cyan-900 leading-relaxed">
            <div className="font-semibold mb-1">What the data shows</div>
            <p className="mb-2">
              Officers who tried Enhanced Writing came back to it —
              <b> {avgActionsPerCase.toFixed(1)} enhancements per case on
              average</b>, often re-running the feature multiple times in the same draft to
              tighten different paragraphs. That's a usage shape consistent with a feature people
              actually like, not a one-time curiosity.
            </p>
            {labels.isAnnual ? (
              <p>
                Even so, Enhanced Writing usage has room to grow across the department —
                the {bundle.excluded.cases.toLocaleString()} reports still typed without
                any AI assistance (see exec summary) point to a clear training opportunity
                that would add hours of savings on top of the current total.
              </p>
            ) : (
              <p>
                The flip side: only <b>{enhancedWriting.officers} of {summary.active_officers}</b>{" "}
                active officers tried it at all. Combined with the {bundle.excluded.cases} reports
                still typed without any AI assistance (see exec summary), there's a clear
                opportunity for a short training session to broaden adoption — and add hours of
                savings on top of the current total.
              </p>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ================== Suggestions feature usage ====================

function SuggestionsUsageSection({ bundle }: { bundle: StudyBundle }) {
  const usage = bundle.suggestionsUsage;
  const labels = getPeriodLabels(bundle);
  // baseNum 11 is reserved for this section in SECTION_DEFS; the display
  // ordinal slots in between Enhanced Writing and Top Officers.
  const num = useSectionNum(11);

  if (!usage) return null;

  const totalCasesAll = bundle.summary.total_cases_all;
  const implementedPctOfAll =
    totalCasesAll > 0 ? (usage.implemented_cases / totalCasesAll) * 100 : 0;
  const addressPctOfAll =
    totalCasesAll > 0 ? (usage.address_cases / totalCasesAll) * 100 : 0;
  const typedWithSuggestionUse = usage.typed_cases_with_suggestion_use;

  return (
    <Section
      id="sec-suggestions"
      number={String(num)}
      title="Suggestions feature usage"
      subtitle={`After every AI-drafted report, the platform surfaces inline suggestions — missing details, formatting fixes, follow-up questions. Officers either check them off as they're addressed, or hit "Address Suggestions" to dictate the answers and let the AI weave them in. Both signal active engagement with the report after the initial draft.`}
    >
      {(() => {
        // Hide any stat whose primary value is 0 so empty signals don't
        // dilute the section — if no one used a feature, omit it entirely.
        // Grid column count adapts to however many stats remain.
        const stats: React.ReactNode[] = [];
        if (usage.implemented_total > 0) {
          stats.push(
            <HeroStat
              key="implemented_total"
              icon={<CheckCircle2 size={18} />}
              label="Suggestions checked off"
              value={usage.implemented_total.toLocaleString()}
              sub={`across ${usage.implemented_cases.toLocaleString()} case${
                usage.implemented_cases === 1 ? "" : "s"
              }`}
            />
          );
        }
        if (usage.implemented_officers > 0) {
          stats.push(
            <HeroStat
              key="implemented_officers"
              icon={<Users size={18} />}
              label="Officers reviewing"
              value={usage.implemented_officers.toLocaleString()}
              sub="checked off ≥1 suggestion"
            />
          );
        }
        if (usage.address_total > 0) {
          stats.push(
            <HeroStat
              key="address_total"
              icon={<MessageSquareReply size={18} />}
              label="Address Suggestions used"
              value={usage.address_total.toLocaleString()}
              sub={`across ${usage.address_cases.toLocaleString()} case${
                usage.address_cases === 1 ? "" : "s"
              }`}
            />
          );
        }
        if (usage.address_officers > 0) {
          stats.push(
            <HeroStat
              key="address_officers"
              icon={<Users size={18} />}
              label="Officers dictating answers"
              value={usage.address_officers.toLocaleString()}
              sub="used Address Suggestions"
            />
          );
        }
        if (stats.length === 0) return null;
        const cols = Math.min(stats.length, 4);
        return (
          <div
            className={`grid grid-cols-2 gap-3 mb-6 ${
              cols === 1
                ? "md:grid-cols-1"
                : cols === 2
                ? "md:grid-cols-2"
                : cols === 3
                ? "md:grid-cols-3"
                : "md:grid-cols-4"
            }`}
          >
            {stats}
          </div>
        );
      })()}

      <div className="grid md:grid-cols-2 gap-3">
        {usage.implemented_cases > 0 && (
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={16} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-900">
                Checked off
              </h3>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              Suggestions the officer reviewed and marked as addressed in the
              editor — the AI flagged something, the officer confirmed it was
              either already covered or fixed it in the draft. Logged when
              <code className="text-[11px] bg-slate-100 px-1 rounded mx-1">
                suggestions.implemented
              </code>
              is toggled to true.
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {implementedPctOfAll.toFixed(1)}% of all {totalCasesAll.toLocaleString()} reports written in
              {" "}{labels.periodNoun} had at least one suggestion checked off.
            </div>
            <div className="mt-3 text-xs text-slate-500 italic">
              Note: this is a floor, not a ceiling. Officers frequently
              incorporate suggestions into the draft without explicitly
              toggling the checkbox — actual engagement with the feature is
              higher than the number captured here.
            </div>
          </div>
        )}
        {usage.address_cases > 0 && (
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquareReply size={16} className="text-cyan-700" />
              <h3 className="text-sm font-semibold text-slate-900">
                Address Suggestions
              </h3>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              The officer recorded a single piece of follow-up audio answering
              the open suggestions, and the AI rewrote the relevant sections of
              the draft to incorporate the new details. Logged as
              <code className="text-[11px] bg-slate-100 px-1 rounded mx-1">
                suggestions_insert
              </code>
              audit entries.
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {addressPctOfAll.toFixed(1)}% of all reports used Address
              Suggestions to batch-resolve open feedback.
            </div>
          </div>
        )}
      </div>

      {typedWithSuggestionUse > 0 && (
        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
          <Info className="text-amber-700 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-amber-900 leading-relaxed">
            <div className="font-semibold mb-1">
              Typed doesn't always mean no AI assistance
            </div>
            <p>
              {typedWithSuggestionUse === 1 ? "This report was" : `These ${typedWithSuggestionUse.toLocaleString()} reports were`}{" "}
              drafted without audio, but the officer still engaged the
              suggestions feature on{" "}
              {typedWithSuggestionUse === 1 ? "it" : "them"} — checking off
              feedback or dictating answers to surface gaps. They count toward
              AI-assisted reports above, not the typed total in §3.
            </p>
          </div>
        </div>
      )}

      {usage.address_cases === 0 && (
        <div className="mt-6 rounded-md border border-cyan-200 bg-cyan-50 p-5 flex items-start gap-3">
          <Sparkles className="text-cyan-700 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-cyan-900 leading-relaxed">
            <div className="font-semibold mb-1">
              Opportunity: Address Suggestions wasn't used
            </div>
            <p>
              Address Suggestions is one of the highest-leverage features in
              the platform — instead of typing answers to every open
              suggestion, an officer records a single piece of follow-up
              audio and the AI weaves the new details into the right
              sections of the draft. A short walk-through during the next
              training block would unlock a meaningful jump in report
              quality with no additional officer effort beyond hitting one
              button.
            </p>
          </div>
        </div>
      )}
    </Section>
  );
}

// ================== 5. Top officers ====================

// "Substantial usage" floor: an officer has to have written at least this many
// AI-assisted reports during the trial to appear on the leaderboard. Below
// this they either signed up but never really tried the platform, or wrote a
// single exploratory report. For very small pilots (a 3- or 5-seat trial)
// almost no one will clear the floor, so we fall back to anyone with at
// least one AI-assisted case below.
const SUBSTANTIAL_USAGE_MIN_CASES = 3;
// Hard cap on how many officers we render in the leaderboard — keeps the
// section readable for departments with hundreds of seats.
const LEADERBOARD_MAX = 10;

function OfficersSection({ bundle }: { bundle: StudyBundle }) {
  const { byOfficer } = bundle;
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(5);

  // Only officers who actually used the AI features count — typed-only
  // officers don't have a meaningful "time saved" number to rank by.
  const sorted = [...byOfficer]
    .filter((o) => o.cases > 0)
    .sort((a, b) => b.saved_min - a.saved_min || b.total_cases - a.total_cases);

  // Apply the substantial-usage floor; if that leaves fewer than 3 officers
  // (small pilots), drop the floor so the section still has something to
  // show. We surface the chosen floor in the subtitle so the reader knows
  // what bar was applied.
  const substantial = sorted.filter((o) => o.cases >= SUBSTANTIAL_USAGE_MIN_CASES);
  const usingFallback = substantial.length < 3;
  const filtered = usingFallback ? sorted : substantial;
  const effectiveMin = usingFallback ? 1 : SUBSTANTIAL_USAGE_MIN_CASES;

  const leaderboard = filtered.slice(0, LEADERBOARD_MAX);
  const podiumCount = Math.min(3, leaderboard.length);
  const podium = leaderboard.slice(0, podiumCount);
  // When everyone fits in the podium, the bar leaderboard would just repeat
  // the same officers — skip it.
  const showLeaderboard = leaderboard.length > podiumCount;

  // Annual reports drop the "X of Y active officers" comparison so the
  // exact adoption width isn't surfaced — partial adoption is normal for
  // active departments and we don't want the number used to argue for
  // seat reductions.
  const subtitle = labels.isAnnual
    ? usingFallback
      ? `Ranked by total estimated time saved across ${labels.periodNoun}. Includes every officer with at least one AI-assisted report.`
      : `Ranked by total estimated time saved across ${labels.periodNoun}. To keep the focus on officers who actively used the platform, this list includes only those with at least ${effectiveMin} AI-assisted reports.`
    : usingFallback
      ? `Ranked by total estimated time saved across ${labels.periodNoun}. Includes every officer with at least one AI-assisted report${
          sorted.length === 0 ? "" : ` (${sorted.length} total)`
        }.`
      : `Ranked by total estimated time saved across ${labels.periodNoun}. To keep the focus on officers who actively used the platform, this list includes only those with at least ${effectiveMin} AI-assisted reports — ${leaderboard.length} of ${sorted.length} active officer${sorted.length === 1 ? "" : "s"} cleared that bar.`;

  if (leaderboard.length === 0) {
    return (
      <Section
        id="sec-5"
        number={String(num)}
        title="Top officers"
        subtitle={`Ranked by total estimated time saved across ${labels.periodNoun}.`}
      >
        <div className="rounded-md border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
          No officers have written AI-assisted reports yet during {labels.periodNoun}.
        </div>
      </Section>
    );
  }

  // Match the podium count to the actual number of cards so single-officer
  // and two-officer pilots don't render a half-empty 3-column row.
  const podiumGridCls =
    podiumCount === 1
      ? "grid grid-cols-1 max-w-md mx-auto gap-3 mb-6"
      : podiumCount === 2
        ? "grid grid-cols-1 md:grid-cols-2 gap-3 mb-6"
        : "grid grid-cols-1 md:grid-cols-3 gap-3 mb-6";

  return (
    <Section id="sec-5" number={String(num)} title="Top officers" subtitle={subtitle}>
      <div className={podiumGridCls}>
        {podium.map((o, i) => (
          <div
            key={o.user_id}
            className={`rounded-md p-4 border ${
              i === 0
                ? "border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50"
                : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex items-baseline justify-between">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {i === 0 ? "🥇 Top performer" : i === 1 ? "🥈 Runner-up" : "🥉 Third"}
              </div>
              <div className="text-xs text-slate-400 tabular-nums">
                {o.total_cases} reports
              </div>
            </div>
            <div className="mt-2 text-base font-semibold text-slate-900 truncate">
              {o.officer_name}
            </div>
            <div className="text-xs text-slate-500">{o.badge_id ? `#${o.badge_id}` : ""}</div>
            <div className="mt-3 text-2xl font-bold tabular-nums text-emerald-700">
              {(o.saved_min / 60).toFixed(1)} hr
            </div>
            <div className="text-[11px] text-slate-500">
              <b>{o.cases}</b> AI-assisted
              {o.typed_cases > 0 && <> · {o.typed_cases} typed</>}
              {!bundle.obscure_audio_split &&
                o.interaction_files > 0 &&
                ` · ${o.interaction_files} interaction file${o.interaction_files === 1 ? "" : "s"}`}
            </div>
          </div>
        ))}
      </div>

      {showLeaderboard && (
        <div className="border border-slate-200 rounded-md p-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">
              {usingFallback
                ? `All active officers (${leaderboard.length})`
                : `Officers with substantial usage (${leaderboard.length})`}
            </h3>
            <div className="text-[11px] text-slate-500">
              Bar shows hours saved · in-bar number is words generated
            </div>
          </div>
          <OfficerLeaderboard data={leaderboard} limit={leaderboard.length} />
        </div>
      )}
    </Section>
  );
}

// ================== 6. Case index ====================

// ================== Officer survey ====================

const SURVEY_TOP_N = 5;

function SurveyBar({ label, count, pct, max }: { label: string; count: number; pct: number; max: number }) {
  const width = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-48 md:w-56 shrink-0 truncate text-slate-700" title={label}>
        {label}
      </div>
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${width}%` }} />
      </div>
      <div className="w-14 shrink-0 text-right tabular-nums text-slate-500 text-xs">{pct}%</div>
    </div>
  );
}

function SurveyBarGroup({
  title,
  note,
  options,
}: {
  title: string;
  note?: string;
  options: { value: string; label: string; count: number; pct: number }[];
}) {
  const shown = options.filter((o) => o.count > 0);
  if (shown.length === 0) return null;
  const max = Math.max(...shown.map((o) => o.count));
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        {note && <span className="text-[11px] text-slate-500">{note}</span>}
      </div>
      <div className="space-y-2">
        {shown.map((o) => (
          <SurveyBar key={o.value} label={o.label} count={o.count} pct={o.pct} max={max} />
        ))}
      </div>
    </div>
  );
}

function SurveySection({ bundle }: { bundle: StudyBundle }) {
  const survey = bundle.survey!;
  const num = useSectionNum(12);
  const labels = getPeriodLabels(bundle);
  const activeOfficers = bundle.summary.total_officers_all;
  const responseRate =
    activeOfficers > 0 ? Math.round((survey.responses / activeOfficers) * 100) : null;
  const timeSavedAnswered = survey.per_question.time_saved_per_report?.answered ?? 0;

  return (
    <Section
      id="sec-survey"
      number={String(num)}
      title="What officers said"
      subtitle={`At the end of ${labels.periodNoun}, officers were asked a short survey about report quality, time saved, and ease of use. ${survey.responses} officer${
        survey.responses === 1 ? "" : "s"
      } responded${
        responseRate !== null && !labels.isAnnual
          ? ` (${responseRate}% of the ${activeOfficers} who used the platform)`
          : ""
      }. Percentages are of officers who answered each question.`}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <HeroStat
          icon={<Users size={18} />}
          label="Would keep it"
          value={survey.keep.pct_yes_or_probably !== null ? `${survey.keep.pct_yes_or_probably}%` : "—"}
          sub="answered yes or probably"
          accent={(survey.keep.pct_yes_or_probably ?? 0) >= 50}
        />
        <HeroStat
          icon={<Gauge size={18} />}
          label="Overall rating"
          value={survey.avg_overall !== null ? survey.avg_overall.toFixed(1) : "—"}
          unit="/ 5"
          sub={survey.avg_ease !== null ? `ease of use ${survey.avg_ease.toFixed(1)} / 5` : undefined}
        />
        <HeroStat
          icon={<TrendingUp size={18} />}
          label="Net Promoter Score"
          value={survey.nps.score !== null ? String(survey.nps.score) : "—"}
          sub={
            survey.nps.answered > 0
              ? `${survey.nps.promoters} promoter${survey.nps.promoters === 1 ? "" : "s"} · ${survey.nps.detractors} detractor${survey.nps.detractors === 1 ? "" : "s"}`
              : undefined
          }
        />
        <HeroStat
          icon={<Clock size={18} />}
          label="Saved per report"
          value={survey.time_saved.weighted_avg_min !== null ? String(survey.time_saved.weighted_avg_min) : "—"}
          unit="min"
          sub="officer-reported, weighted average"
        />
      </div>

      {survey.ai_attitude.pct_open_before !== null && survey.ai_attitude.pct_open_after !== null && (
        <div className="rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-5 mb-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
            Attitude toward AI report writing
          </div>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div>
              <span className="text-2xl md:text-[28px] font-bold tabular-nums text-slate-900">
                {survey.ai_attitude.pct_open_before}%
              </span>
              <span className="text-sm text-slate-600 ml-1.5">open to it before the trial</span>
            </div>
            <span className="text-slate-400">→</span>
            <div>
              <span className="text-2xl md:text-[28px] font-bold tabular-nums text-emerald-700">
                {survey.ai_attitude.pct_open_after}%
              </span>
              <span className="text-sm text-slate-600 ml-1.5">open to it after</span>
            </div>
            {survey.ai_attitude.skeptics > 0 && survey.ai_attitude.pct_skeptics_won_over !== null && (
              <div className="text-sm text-slate-700 md:ml-auto">
                <b className="text-slate-900">
                  {survey.ai_attitude.skeptics_won_over} of {survey.ai_attitude.skeptics}
                </b>{" "}
                officers who started unsure, skeptical, or against it are now open to it.
              </div>
            )}
          </div>
        </div>
      )}

      {(survey.pct_quality_better !== null || survey.pct_overtime_reduced !== null) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {survey.pct_quality_better !== null && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <b className="text-slate-900">{survey.pct_quality_better}%</b> said their reports are
              better than the ones they write without the platform.
            </div>
          )}
          {survey.pct_overtime_reduced !== null && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <b className="text-slate-900">{survey.pct_overtime_reduced}%</b> of officers who work
              report overtime said it went down.
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SurveyBarGroup
          title="Time saved per report"
          note={`${timeSavedAnswered} answered`}
          options={survey.time_saved.buckets}
        />
        <SurveyBarGroup
          title="Most useful features"
          note="each officer picked up to 4"
          options={survey.top_features.slice(0, SURVEY_TOP_N)}
        />
        <SurveyBarGroup title="Where the recovered time went" options={survey.time_reinvested} />
        <SurveyBarGroup title="What held officers back" options={survey.concerns.slice(0, SURVEY_TOP_N)} />
      </div>

      {survey.quotes.length > 0 && (
        <SubSection number={`${num}.1`} title="In their words">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {survey.quotes.map((q, i) => (
              <blockquote
                key={i}
                className="rounded-md border-l-4 border-blue-300 bg-slate-50 px-5 py-4 m-0"
              >
                <Quote size={16} className="text-blue-400 mb-2" />
                <p className="text-slate-800 leading-relaxed m-0">{q.text}</p>
                <footer className="mt-3 text-xs text-slate-500">
                  — {[q.rank, q.name].filter(Boolean).join(" ") || `${bundle.agency.name} officer`}
                </footer>
              </blockquote>
            ))}
          </div>
        </SubSection>
      )}
    </Section>
  );
}

function CaseIndexSection({
  bundle,
  onSelect,
}: {
  bundle: StudyBundle;
  onSelect: (id: string) => void;
}) {
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(6);
  return (
    <Section
      id="sec-6"
      number={String(num)}
      title="Case index"
      subtitle={`Every AI-assisted report written during ${labels.periodNoun}. Open a case to see exactly how the time-savings number was computed and which AI features were used.`}
    >
      <CaseList
        cases={bundle.cases}
        onSelect={onSelect}
        obscureAudioSplit={bundle.obscure_audio_split}
      />
    </Section>
  );
}

// ================== 7. Methodology ====================

function MethodologySection({ bundle }: { bundle: StudyBundle }) {
  const { constants } = bundle;
  const labels = getPeriodLabels(bundle);
  const num = useSectionNum(7);

  return (
    <Section
      id="sec-7"
      number={String(num)}
      title="Methodology"
      subtitle={`How the time-savings figures were computed. All numbers come from the agency's ${
        labels.isAnnual ? "report database" : "trial database"
      }; the only modeling assumptions are the three constants below.`}
    >
      <SubSection number={`${num}.1`} title="The model">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-5">
          <div className="flex items-center gap-2 text-slate-700 mb-3">
            <Calculator size={16} />
            <span className="text-sm font-semibold">Per-case time saved</span>
          </div>
          <pre className="bg-white border border-slate-200 rounded p-4 text-sm font-mono text-slate-800 overflow-x-auto leading-relaxed">
{`manual_min  = ${constants.setup_min_per_report ?? 30}                                // per-report setup overhead
            + words / ${constants.typing_wpm}                     // writing/dictation at ${constants.typing_wpm} WPM
            + interaction_audio_min / ${constants.relisten_speed}     // re-listen at ${constants.relisten_speed}× speed
ai_min      = ${constants.review_min_per_case}                                 // flat verification overhead
saved_min   = manual_min - ai_min`}
          </pre>
        </div>
      </SubSection>

      <SubSection number={`${num}.2`} title="Constants — and why we chose them">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-2 leading-relaxed">
          <li>
            <b>
              Manual report time = {constants.setup_min_per_report ?? 30} min baseline +{" "}
              {constants.typing_wpm} WPM writing.
            </b>{" "}
            Report writing has a fixed component (pulling up the case, running subjects,
            gathering attached evidence, navigating between RMS and the editor, formatting per
            agency standards, signing off) and a length-dependent component (composing the
            actual narrative). The combination is calibrated against what departments
            consistently report on calls: a typical ~360-word report takes about 45 minutes
            end-to-end. Officers can type at 40+ WPM, but the bulk of report-writing time is
            the fixed work — which is why even a short report rarely takes less than half an
            hour, and a long one doesn't take an entire shift.
          </li>
          <li>
            <b>{constants.review_min_per_case} minutes per case</b> for officer verification of
            an AI-drafted narrative. This includes reading the draft, fixing any errors, and
            signing off. Per-case review is roughly the same regardless of report length.
          </li>
          <li>
            <b>{constants.relisten_speed}× playback speed</b> for re-listening to interaction
            audio. Officers reviewing recorded interviews almost always speed up playback;{" "}
            {constants.relisten_speed}× is on the slower end of typical review speeds.
          </li>
        </ul>
      </SubSection>

      <SubSection number={`${num}.3`} title="Audio duration estimation">
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          The database stores audio file size but not duration, so we estimate duration
          from file size and format. m4a/mp3 voice recordings are assumed to be 64 kbps AAC/MP3,
          wav files are assumed to be 16-bit/16 kHz mono PCM (32 KB/s), and mp4/mov body cam
          video is assumed to be ~2 Mbps. These match the defaults of the recording pipelines
          used by the agency. Estimates may be off by ±10% on individual files, but average out
          across the files in {labels.periodNoun}.
        </p>
      </SubSection>

      <SubSection
        number={`${num}.4`}
        title="Which cases count"
        subtitle="A case counts toward the time-savings totals only if the platform's AI was demonstrably used on it."
      >
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          <li>
            <b>Included:</b> any case with audio captured (narrative or interaction), or any
            audit-log entry of type <code className="text-xs bg-slate-100 px-1 rounded">first_draft</code>,{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">enhanced_dictation</code>,{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">pure_dictation</code>,{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">enhanced_writing</code>, or{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">suggestions_insert</code>, or
            any case where the officer checked off a suggestion (
            <code className="text-xs bg-slate-100 px-1 rounded">
              suggestions.implemented = true
            </code>
            ) or had an AI quality score generated.
          </li>
          <li>
            <b>Excluded:</b> {bundle.excluded.cases} cases that had no audio, no AI audit
            entries, no checked-off suggestions, and no AI score — the officer typed the
            report into the editor and never invoked any AI feature on it. These do not
            count toward the agency time-savings figure.
          </li>
        </ul>
      </SubSection>

      <SubSection number={`${num}.5`} title="What is excluded from the savings model">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          <li>
            Time saved on supervisor review (reports are more consistent and need fewer revisions).
          </li>
          <li>
            Reduced overtime spent at the end of shift writing reports.
          </li>
          <li>
            Faster turnaround on prosecution-ready reports.
          </li>
          <li>
            Mobile-first capture, which lets officers complete reports in-field rather than at
            the station.
          </li>
        </ul>
        <p className="text-sm text-slate-500 italic mt-3">
          The {(bundle.summary.saved_min_total / 60).toFixed(0)}-hour figure is conservative —
          it counts only what we can directly measure from the database.
        </p>
      </SubSection>
    </Section>
  );
}

// ================== 8. Cost projection ====================

function CostProjectionSection({ bundle }: { bundle: StudyBundle }) {
  const { summary } = bundle;
  const num = useSectionNum(8);
  const labels = getPeriodLabels(bundle);

  // Salary basis: prefer the bundle's per-agency value if present, else the
  // module-level fallback (which matches the original Summit County figures).
  const primarySalary = bundle.salaryBasis?.primary.salary ?? FALLBACK_PRIMARY_SALARY;
  const primaryLabel = bundle.salaryBasis?.primary.label ?? FALLBACK_PRIMARY_LABEL;
  const primarySource = bundle.salaryBasis?.primary.source ?? FALLBACK_PRIMARY_SOURCE;
  const comparison = bundle.salaryBasis?.comparison;

  // Editable assumptions — knobs the user can turn live. The headcount
  // defaults to the agency's actual force size when the bundle provides it,
  // otherwise to the global default (70).
  const defaultOfficers = bundle.agency.total_officers ?? DEFAULT_PROJECTION_OFFICERS;
  const [officers, setOfficers] = useState(defaultOfficers);
  const [salary, setSalary] = useState(primarySalary);
  const [otMixPct, setOtMixPct] = useState(DEFAULT_OT_MIX_PCT);

  // Reporting period in months — first to last AI-assisted case timestamp.
  // For trial reports this is the trial window; for annual reports this is
  // the user-picked review window.
  const periodMonths =
    (new Date(summary.last_case).getTime() - new Date(summary.first_case).getTime()) /
    (1000 * 60 * 60 * 24 * 30.44);

  // Conservative basis: average pace of the top 5 officers from the period
  // — the actively engaged users, so this is "what an engaged officer
  // typically achieves" rather than the agency-wide average (which is
  // dragged down by officers who barely used the platform).
  const top5 = useMemo(
    () => [...bundle.byOfficer].sort((a, b) => b.saved_min - a.saved_min).slice(0, 5),
    [bundle.byOfficer]
  );
  const top5TotalHr = top5.reduce((s, o) => s + o.saved_min / 60, 0);
  const top5AvgHrPerOfficer = top5.length > 0 ? top5TotalHr / top5.length : 0;
  const top5HrPerMonth = periodMonths > 0 ? top5AvgHrPerOfficer / periodMonths : 0;

  // Ceiling basis: the single most engaged officer in the period.
  const topOfficer = top5[0];
  const topHrPerMonth =
    topOfficer && periodMonths > 0 ? topOfficer.saved_min / 60 / periodMonths : 0;

  // Project to {officers} officers over a full year.
  const baselineHrPerYear = top5HrPerMonth * 12 * officers;
  const topAdoptionHrPerYear = topHrPerMonth * 12 * officers;

  // Hourly labor rates.
  const hourlyRate = salary / HOURS_PER_WORK_YEAR;
  const overtimeRate = hourlyRate * OT_MULTIPLIER;

  // Blended rate when {otMixPct}% of recovered hours would have been overtime.
  const otMix = otMixPct / 100;
  const blendedRate = hourlyRate * (1 - otMix) + overtimeRate * otMix;

  const baselineDollars = baselineHrPerYear * hourlyRate;
  const topAdoptionDollars = topAdoptionHrPerYear * hourlyRate;
  // Third scenario: same hours as the ceiling, but valued at the OT-blended rate
  // because much of the recovered time would have been overtime.
  const overtimeDollars = topAdoptionHrPerYear * blendedRate;

  const fmt$ = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  // Hours actually recovered over the review window — surfaced in the
  // "Already recovered" card for annual reports.
  const actualSavedHr = summary.saved_min_total / 60;

  // Status-quo trajectory (annual only): what the agency is on pace to
  // recover this year if NOTHING changes — same adoption, same engagement,
  // just extrapolated from the actual hours over the review window. This
  // is the floor that anchors the projection cards below; the gap between
  // this and the three scenarios is the value of expanding adoption.
  const statusQuoHrPerYear =
    periodMonths > 0 ? (actualSavedHr * 12) / periodMonths : 0;
  const statusQuoDollars = statusQuoHrPerYear * hourlyRate;
  const statusQuoDollarsBlended = statusQuoHrPerYear * blendedRate;

  // Headroom — how much more $ each scenario unlocks vs. status quo. The
  // OT-aware card compares to the blended status-quo number because both
  // sides of the subtraction need to use the same rate basis.
  const headroomBaseline = Math.max(0, baselineDollars - statusQuoDollars);
  const headroomTopAdoption = Math.max(0, topAdoptionDollars - statusQuoDollars);
  const headroomOvertime = Math.max(
    0,
    overtimeDollars - statusQuoDollarsBlended
  );

  return (
    <Section
      id="sec-8"
      number={String(num)}
      title={
        labels.isAnnual
          ? "Cost recovered + headroom"
          : "Cost projection at department scale"
      }
      subtitle={
        labels.isAnnual
          ? "What the agency has already banked, what current pace yields over a full year, and the additional value available as adoption broadens across the department. Adjust the headcount and average salary to model your own scenario."
          : "What the observed efficiency would be worth applied to a full department over a year. Adjust the headcount and average salary below to model your own scenario."
      }
    >
      {labels.isAnnual && (
        <div className="mb-6 grid md:grid-cols-2 gap-4">
          {/* Already recovered — actual hours over the review window. */}
          <div className="rounded-md border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-blue-50 p-5 relative">
            <div className="absolute -top-2.5 left-4 px-2 bg-emerald-500 text-white text-[10px] uppercase tracking-wider font-bold rounded">
              Already recovered
            </div>
            <div className="mt-1">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-emerald-700" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                  Hours saved
                </span>
              </div>
              <div className="text-4xl font-bold tabular-nums text-emerald-700 mt-2">
                {actualSavedHr.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                <span className="text-lg font-medium text-emerald-700/70 ml-2">hr</span>
              </div>
              <div className="text-xs text-emerald-800/70 mt-1">
                across the review window ({periodMonths.toFixed(1)} mo)
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-emerald-200 text-xs text-emerald-900/70 leading-relaxed">
              Officer-hours the agency has already banked — not a projection.
            </div>
          </div>

          {/* Status-quo trajectory — what the review-window pace yields
              annualized, at the agency's current adoption and engagement.
              This is the floor for the headroom comparison below. */}
          <div className="rounded-md border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 relative">
            <div className="absolute -top-2.5 left-4 px-2 bg-blue-500 text-white text-[10px] uppercase tracking-wider font-bold rounded">
              Status-quo trajectory
            </div>
            <div className="mt-1">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={16} className="text-blue-700" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-blue-700">
                  At this pace, full year
                </span>
              </div>
              <div className="text-4xl font-bold tabular-nums text-blue-700 mt-2">
                {fmt$(statusQuoDollars)}
                <span className="text-lg font-medium text-blue-700/70 ml-2">/ yr</span>
              </div>
              <div className="text-xs text-blue-800/70 mt-1">
                ≈ {Math.round(statusQuoHrPerYear).toLocaleString()} hr/yr ·{" "}
                {fmt$(statusQuoDollarsBlended)} OT-blended
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200 text-xs text-blue-900/70 leading-relaxed">
              The review-window pace ({actualSavedHr.toFixed(0)} hr ÷ {periodMonths.toFixed(1)} mo
              × 12) annualized at current adoption — no behavior change required.
            </div>
          </div>
        </div>
      )}

      <SubSection
        number={`${num}.1`}
        title="Your assumptions"
        subtitle={
          bundle.agency.total_officers
            ? `Defaults to the agency's full ${bundle.agency.total_officers}-officer roster valued at the published average. Edit any box to model a different scenario.`
            : "Defaults to a 70-officer rollout valued at the published agency average. Edit any box to model your own scenario."
        }
      >
        <div className="rounded-md border border-blue-200 bg-blue-50/60 p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <NumberInput
              label="Number of officers"
              value={officers}
              onChange={setOfficers}
              min={1}
              max={5000}
              step={1}
              prefix={<Users size={14} />}
              hint={`Total sworn officers on the force — the projection scales the per-officer pace from ${labels.periodNoun} across this many.`}
            />
            <NumberInput
              label="Average officer salary"
              value={salary}
              onChange={setSalary}
              min={0}
              max={500000}
              step={1000}
              format="dollar"
              prefix={<DollarSign size={14} />}
              hint={`= $${hourlyRate.toFixed(2)} / hr @ ${HOURS_PER_WORK_YEAR.toLocaleString()} work hrs/yr`}
            />
            <NumberInput
              label="% of saved hours that are overtime"
              value={otMixPct}
              onChange={setOtMixPct}
              min={0}
              max={100}
              step={5}
              format="percent"
              prefix={<TrendingUp size={14} />}
              hint={`OT paid at ${OT_MULTIPLIER}× = $${overtimeRate.toFixed(2)} / hr · blended = $${blendedRate.toFixed(2)} / hr`}
            />
          </div>
        </div>
      </SubSection>

      <SubSection
        number={`${num}.2`}
        title={
          labels.isAnnual
            ? "Available headroom under broader adoption"
            : "Three scenarios"
        }
        subtitle={
          labels.isAnnual
            ? `These three figures are not assumptions about future behavior — they are the per-officer pace from ${labels.periodNoun} applied at the full headcount above. The gap between each figure and the status-quo trajectory above is the value of expanding adoption across the department.`
            : `All three projections scale up the observed time savings — they are not assumptions about future behavior, they are the per-officer rate from ${labels.periodNoun} applied at the headcount above. The overtime card values the same hours at a blended rate that accounts for report time pushing officers past end-of-shift.`
        }
      >
        <div className="grid md:grid-cols-3 gap-4">
          {/* Conservative — top-5 average pace */}
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} className="text-slate-500" />
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {labels.isAnnual ? "Headroom · top-5 pace" : "Conservative · top-5 avg pace"}
              </span>
            </div>
            <div className="text-3xl font-bold tabular-nums text-slate-900 mt-2">
              {fmt$(baselineDollars)}
              <span className="text-base font-medium text-slate-500 ml-1">/ year</span>
            </div>
            <div className="text-sm text-slate-600 mt-1">
              ≈ <b>{Math.round(baselineHrPerYear).toLocaleString()} officer-hours</b> recovered
            </div>
            {labels.isAnnual && (
              <div className="mt-2 inline-block px-2 py-0.5 rounded bg-slate-100 text-[11px] font-semibold text-slate-700">
                + {fmt$(headroomBaseline)} over status quo
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                {labels.isAnnual ? "Top 5 users this year" : "Top 5 trial users"}
              </div>
              <div className="space-y-1 text-xs">
                {top5.map((o) => (
                  <div key={o.user_id} className="flex justify-between">
                    <span className="text-slate-700">{o.officer_name}</span>
                    <span className="font-mono text-slate-700 tabular-nums">
                      {(o.saved_min / 60).toFixed(1)} hr
                    </span>
                  </div>
                ))}
                <div className="flex justify-between pt-1 mt-1 border-t border-slate-200">
                  <span className="font-semibold text-slate-800">Combined</span>
                  <span className="font-mono font-semibold text-slate-800 tabular-nums">
                    {top5TotalHr.toFixed(1)} hr
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 space-y-1.5 leading-relaxed font-mono">
              <div>
                {top5TotalHr.toFixed(1)} hr ÷ 5 officers ={" "}
                <span className="text-slate-800 font-semibold">
                  {top5AvgHrPerOfficer.toFixed(1)} hr / officer
                </span>
                <span className="text-slate-400 ml-1">(over {periodMonths.toFixed(1)} mo)</span>
              </div>
              <div>
                × (12 ÷ {periodMonths.toFixed(1)}) ={" "}
                <span className="text-slate-800 font-semibold">
                  {(top5HrPerMonth * 12).toFixed(1)} hr / officer / year
                </span>
              </div>
              <div>
                × {officers.toLocaleString()} officers ={" "}
                <span className="text-slate-800 font-semibold">
                  {Math.round(baselineHrPerYear).toLocaleString()} hr / year
                </span>
              </div>
            </div>
          </div>

          {/* Top-performer ceiling */}
          {topOfficer && (
            <div className="rounded-md border border-emerald-300 bg-gradient-to-br from-emerald-50 to-blue-50 p-5">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={16} className="text-emerald-700" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                  {labels.isAnnual
                    ? "Headroom · top-performer pace"
                    : "Realistic savings · top-performer pace"}
                </span>
              </div>
              <div className="text-3xl font-bold tabular-nums text-emerald-700 mt-2">
                {fmt$(topAdoptionDollars)}
                <span className="text-base font-medium text-emerald-700/70 ml-1">/ year</span>
              </div>
              <div className="text-sm text-emerald-800/80 mt-1">
                ≈ <b>{Math.round(topAdoptionHrPerYear).toLocaleString()} officer-hours</b> recovered
              </div>
              {labels.isAnnual && (
                <div className="mt-2 inline-block px-2 py-0.5 rounded bg-emerald-100 text-[11px] font-semibold text-emerald-800">
                  + {fmt$(headroomTopAdoption)} over status quo
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-emerald-200">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
                  {labels.isAnnual ? "Top performer this year" : "Trial's top performer"}
                </div>
                <div className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-emerald-900">
                      {topOfficer.officer_name}
                      {topOfficer.badge_id && (
                        <span className="text-emerald-900/50 ml-1">#{topOfficer.badge_id}</span>
                      )}
                    </span>
                    <span className="font-mono text-emerald-900/80 tabular-nums">
                      {(topOfficer.saved_min / 60).toFixed(1)} hr saved
                    </span>
                  </div>
                  <div className="flex justify-between text-emerald-900/60">
                    <span>across</span>
                    <span className="font-mono tabular-nums">
                      {topOfficer.cases} cases · {periodMonths.toFixed(1)} mo
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-emerald-200 text-xs text-emerald-900/70 space-y-1.5 leading-relaxed font-mono">
                <div>
                  {(topOfficer.saved_min / 60).toFixed(1)} hr ÷ {periodMonths.toFixed(1)} mo ={" "}
                  <span className="text-emerald-900 font-semibold">
                    {topHrPerMonth.toFixed(2)} hr / month
                  </span>
                </div>
                <div>
                  × 12 ={" "}
                  <span className="text-emerald-900 font-semibold">
                    {(topHrPerMonth * 12).toFixed(1)} hr / officer / year
                  </span>
                </div>
                <div>
                  × {officers.toLocaleString()} officers ={" "}
                  <span className="text-emerald-900 font-semibold">
                    {Math.round(topAdoptionHrPerYear).toLocaleString()} hr / year
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Overtime-aware ceiling */}
          {topOfficer && (
            <div className="rounded-md border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 p-5 relative">
              <div className="absolute -top-2.5 left-4 px-2 bg-amber-400 text-amber-950 text-[10px] uppercase tracking-wider font-bold rounded">
                Overtime-aware
              </div>
              <div className="flex items-center gap-2 mb-1 mt-1">
                <DollarSign size={16} className="text-amber-700" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">
                  {labels.isAnnual
                    ? "Headroom · OT-aware ceiling"
                    : "Including overtime impact"}
                </span>
              </div>
              <div className="text-3xl font-bold tabular-nums text-amber-700 mt-2">
                {fmt$(overtimeDollars)}
                <span className="text-base font-medium text-amber-700/70 ml-1">/ year</span>
              </div>
              <div className="text-sm text-amber-800/80 mt-1">
                Same{" "}
                <b>{Math.round(topAdoptionHrPerYear).toLocaleString()} hours</b> as the ceiling,
                valued at the OT-blended rate
              </div>
              {labels.isAnnual && (
                <div className="mt-2 inline-block px-2 py-0.5 rounded bg-amber-100 text-[11px] font-semibold text-amber-900">
                  + {fmt$(headroomOvertime)} over status quo (blended)
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-amber-200">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 mb-2">
                  Why overtime matters here
                </div>
                <p className="text-xs text-amber-900/80 leading-relaxed">
                  Officers routinely stay past end-of-shift to finish reports — and that time is
                  paid at FLSA overtime ({OT_MULTIPLIER}× base). Hours the platform recovers off
                  the back of a shift convert one-for-one into avoided overtime spend, not just
                  recovered straight-time labor.
                </p>
              </div>

              <div className="mt-4 pt-4 border-t border-amber-200 text-xs text-amber-900/70 space-y-1.5 leading-relaxed font-mono">
                <div>
                  Regular rate:{" "}
                  <span className="text-amber-900 font-semibold">${hourlyRate.toFixed(2)} / hr</span>
                </div>
                <div>
                  OT rate ({OT_MULTIPLIER}×):{" "}
                  <span className="text-amber-900 font-semibold">${overtimeRate.toFixed(2)} / hr</span>
                </div>
                <div>
                  Blended @ {otMixPct}% OT:{" "}
                  <span className="text-amber-900 font-semibold">${blendedRate.toFixed(2)} / hr</span>
                </div>
                <div>
                  × {Math.round(topAdoptionHrPerYear).toLocaleString()} hr ={" "}
                  <span className="text-amber-900 font-semibold">{fmt$(overtimeDollars)} / year</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-slate-500 leading-relaxed">
          {labels.isAnnual ? (
            <>
              Each figure assumes department-wide adoption matches the engagement pace of
              specific officers from {labels.periodNoun}. The top-5 figure is the average of
              the five most engaged users — already proven sustainable. The top-performer
              figure is the pace one officer actually held for the full {periodMonths.toFixed(0)}-month
              period. The OT-aware figure values the same ceiling hours at a blended rate,
              since report-writing time recovered after end-of-shift is overtime that would
              otherwise hit the budget at {OT_MULTIPLIER}×. The headroom badge on each card
              shows the additional value above the status-quo trajectory shown at the top of
              this section — that gap is the recoverable opportunity from broadening adoption.
            </>
          ) : (
            <>
              The conservative figure assumes department-wide adoption matches the average pace of
              the five most engaged officers from {labels.periodNoun} — a realistic floor once the
              workflow is standard practice. The ceiling is the pace one officer actually sustained
              for the full {periodMonths.toFixed(0)}-month period. The overtime-aware
              figure values those ceiling hours at a blended rate, since report-writing time
              recovered after end-of-shift is overtime that would otherwise hit the budget at{" "}
              {OT_MULTIPLIER}×.
            </>
          )}
        </div>
      </SubSection>

      <SubSection
        number={`${num}.3`}
        title="Salary basis"
        subtitle="The hourly rate used above comes directly from the salary box. Default is the published agency average; comparison shown when available."
      >
        <div className="rounded-md border border-slate-200 bg-slate-50 p-5">
          <div className={`grid ${comparison ? "grid-cols-2" : "grid-cols-1"} gap-6`}>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                {primaryLabel} (default)
              </div>
              <div className="text-2xl font-bold tabular-nums text-slate-900">
                {primarySalary.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                <span className="text-sm font-medium text-slate-500 ml-1">/ year</span>
              </div>
              <div className="text-sm text-slate-600 mt-1">
                = <span className="font-mono">${(primarySalary / HOURS_PER_WORK_YEAR).toFixed(2)}</span> / hour
              </div>
              <div className="text-[11px] text-slate-500 mt-2">Source: {primarySource}</div>
            </div>
            {comparison && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                  {comparison.label} (comparison)
                </div>
                <div className="text-2xl font-bold tabular-nums text-slate-700">
                  {comparison.salary.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                  <span className="text-sm font-medium text-slate-500 ml-1">/ year</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  = <span className="font-mono">${(comparison.salary / HOURS_PER_WORK_YEAR).toFixed(2)}</span> / hour
                </div>
                <div className="text-[11px] text-slate-500 mt-2">Source: {comparison.source}</div>
              </div>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="text-xs text-slate-600 leading-relaxed">
              <div className="font-semibold text-slate-700 mb-1">Overtime rate (FLSA standard)</div>
              At ${hourlyRate.toFixed(2)} regular × {OT_MULTIPLIER} ={" "}
              <span className="font-mono font-semibold text-slate-800">
                ${overtimeRate.toFixed(2)} / hr
              </span>
              . Used by the Overtime-aware scenario; the OT-mix slider blends OT and regular
              rates based on what fraction of saved hours would have been worked past
              end-of-shift.
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              <div className="font-semibold text-slate-700 mb-1">Loaded labor cost</div>
              Base salary is the conservative basis. Fully-loaded cost (benefits, employer
              payroll taxes, training, equipment) typically runs 30–40% higher. Multiply the
              figures above by ~1.35 for a budget-realistic estimate.
            </div>
          </div>
        </div>
      </SubSection>

      <SubSection number={`${num}.4`} title="What this number means in practice">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-2 leading-relaxed">
          <li>
            <b>It's labor recovery, not direct cash.</b> Officers don't get paid less. The
            recovered hours redirect from typing reports into other police work — patrol time,
            investigations, community engagement, training, court appearances.
          </li>
          <li>
            <b>The conservative figure is the floor, not the target.</b> It models department-wide
            adoption matching the top-5 average pace from {labels.periodNoun} — already proven
            achievable by five different officers.
          </li>
          <li>
            <b>The overtime-aware card values recovered hours at a blended FLSA rate.</b>{" "}
            Report time pushed past end-of-shift is paid at {OT_MULTIPLIER}×, so hours the
            platform recovers off the back of a shift convert one-for-one into avoided
            overtime spend. Adjust the OT-mix percentage above to match your department's
            actual share of shift-end report writing.
          </li>
        </ul>
      </SubSection>

      <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
        <DollarSign className="text-amber-600 shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-amber-900 leading-relaxed">
          <div className="font-semibold mb-1">Bottom line</div>
          {labels.isAnnual ? (
            <>
              At current adoption, the agency is on pace to recover{" "}
              <b>{fmt$(statusQuoDollars)}/year</b> in officer labor — that's the floor with no
              behavior change. Broadening adoption across the full {officers.toLocaleString()}-officer
              force at the engagement levels already proven during {labels.periodNoun} would unlock an
              additional{" "}
              <b>
                {fmt$(headroomBaseline)}–{fmt$(headroomTopAdoption)} per year
              </b>{" "}
              in straight-time labor, rising to <b>+{fmt$(headroomOvertime)}/year</b> when{" "}
              {otMixPct}% of those hours are valued at FLSA overtime ({OT_MULTIPLIER}×). The
              headroom is the value of expanding adoption, not a hypothetical — every figure scales
              the pace officers already hit during {labels.periodNoun}.
            </>
          ) : (
            <>
              A {officers.toLocaleString()}-officer rollout valued at {fmt$(salary)}/year is
              projected to recover{" "}
              <b>
                {fmt$(baselineDollars)}–{fmt$(topAdoptionDollars)} per year
              </b>{" "}
              in officer labor at straight-time rates. Once you factor in that {otMixPct}% of those
              hours would have been worked at FLSA overtime ({OT_MULTIPLIER}×), the budget impact
              at the ceiling rises to <b>{fmt$(overtimeDollars)} per year</b> — overtime that
              doesn't get spent in the first place.
            </>
          )}
        </div>
      </div>
    </Section>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  prefix,
  hint,
  format,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: React.ReactNode;
  hint?: string;
  format?: "dollar" | "percent";
}) {
  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const n = raw === "" ? 0 : parseFloat(raw);
    if (Number.isFinite(n)) {
      const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));
      onChange(clamped);
    }
  };
  const display = format === "dollar" ? value.toLocaleString("en-US") : String(value);
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">
        {prefix}
        {label}
      </div>
      <div className="relative">
        {format === "dollar" && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold pointer-events-none">
            $
          </span>
        )}
        {format === "percent" && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold pointer-events-none">
            %
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          value={display}
          onChange={handle}
          min={min}
          max={max}
          step={step}
          className={`w-full ${format === "dollar" ? "pl-7" : "pl-3"} ${format === "percent" ? "pr-8" : "pr-3"} py-2.5 bg-white border border-slate-300 rounded-md text-lg font-bold tabular-nums text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        />
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
    </label>
  );
}

// ================== 9. What the trial proved ====================

function ProofSection({ bundle }: { bundle: StudyBundle }) {
  const { summary } = bundle;
  const num = useSectionNum(9);
  const labels = getPeriodLabels(bundle);
  const perOfficerHr =
    summary.active_officers > 0 ? summary.saved_min_total / 60 / summary.active_officers : 0;

  return (
    <Section
      id="sec-9"
      number={String(num)}
      title={labels.proofTitle}
      subtitle={
        labels.isAnnual
          ? "The findings the year's data supports, and where to focus going forward."
          : "The findings the data supports, and the recommended next steps."
      }
    >
      <SubSection number={`${num}.1`} title="Findings">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-2 leading-relaxed">
          <li>
            The platform delivered <b>{(summary.saved_min_total / 60).toFixed(0)} hours</b> of
            recovered time across the agency — averaging{" "}
            <b>{perOfficerHr.toFixed(1)} hours per active officer</b> over {labels.periodNoun}.
          </li>
          {/* LLM-generated, agency-specific middle bullets. The bundle ships
              them as HTML strings (with <b> for emphasis) so we render them
              with dangerouslySetInnerHTML — content is generated server-side
              from a constrained Claude prompt, never user input. */}
          {(bundle.findings ?? []).map((html, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: html }} />
          ))}
          <li>
            Time savings scaled directly with how often each officer reached for the platform —
            the more reports routed through PNAI, the more hours the agency got back.{" "}
            <b>Mandating use as the department standard is the single biggest lever</b>{" "}
            available to multiply {labels.isAnnual ? "this year's" : "the trial's"} results,
            since the cost-projection ceiling assumes every officer writes every report through
            the platform.
          </li>
        </ul>
      </SubSection>

      <SubSection number={`${num}.2`} title="Recommendations">
        <ol className="list-decimal pl-5 text-slate-700 text-sm space-y-2 leading-relaxed">
          <li>
            <b>Mandate platform use for all reports.</b> Time savings scale linearly with case
            volume — every additional report routed through the platform compounds the recovered
            hours. Setting use as the department standard (rather than an optional tool) is what
            turns the conservative cost-projection floor into the baseline and puts the upper
            bound within reach.
          </li>
          <li>
            <b>Standardize on interaction-audio capture for interviews.</b> This workflow produced
            the largest per-case time savings and the most consistent reports.
          </li>
        </ol>
      </SubSection>

      <div className="mt-10 rounded-lg border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
        <ShieldCheck className="text-emerald-600 shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-emerald-900 leading-relaxed">
          <div className="font-semibold mb-1">Risk & liability — secondary benefit</div>
          More consistent report structure, complete details captured automatically from audio,
          and a reviewable AI draft each time an officer writes a report. None of this is in the
          time-savings number above, but all of it materially reduces exposure on
          documentation-driven cases.
        </div>
      </div>
    </Section>
  );
}

// ================== Loading / error ====================

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-slate-900">
      <div className="text-center">
        <Loader2 className="animate-spin mx-auto mb-3 text-slate-400" size={28} />
        <div className="text-sm text-slate-600">Loading findings data…</div>
      </div>
    </div>
  );
}

function NotFoundState({ slug }: { slug: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="max-w-xl bg-white rounded-md border border-slate-200 p-8 text-center">
        <FileText className="text-slate-300 mx-auto mb-4" size={40} />
        <div className="text-xl font-semibold text-slate-900 mb-2">
          No findings page yet
        </div>
        <div className="text-sm text-slate-600 mb-6 leading-relaxed">
          We don't have a generated findings report at this URL. The page
          may not have been created yet, or the link is incorrect.
        </div>
        <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-3 border border-slate-200 text-left">
          <span className="font-semibold text-slate-700">For the team:</span> open
          this agency in <code className="font-mono bg-white px-1">/agency-management</code>,
          go to the <span className="font-semibold">Findings</span> tab, and click{" "}
          <span className="font-semibold">Generate</span>. The page will be available
          at the same URL after generation completes.
          <div className="mt-2 text-slate-400">
            Slug: <code className="font-mono">{slug}</code>
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="max-w-xl bg-white rounded-md border border-red-200 p-6">
        <AlertTriangle className="text-red-500 mb-3" size={24} />
        <div className="text-lg font-semibold text-slate-900 mb-2">Could not load findings data</div>
        <div className="text-sm text-slate-600 mb-4">{message}</div>
        <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-3 border border-slate-200">
          This is a backend/network error, not a missing page. If the issue
          persists, check that the trial-findings API endpoint is reachable and
          that the server has S3 credentials for the
          <code className="font-mono bg-white px-1 mx-1">police-narratives-ai-trial-findings</code>
          bucket.
        </div>
      </div>
    </div>
  );
}
